import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import UserContext from '@/context/users'
import { createAssistant } from '@/services/assistant'
import {
  addAssistantToPlatform,
  createNativeClientPlatform,
  getNativeClientPlatform,
  getNativeClientPlatforms,
} from '@/services/native-client-platform'
import OnboardingShell from './OnboardingShell'
import {
  ONBOARDING_SEEN_KEY,
  STEPS,
  DEFAULT_SETUP_PROGRESS,
  clearOnboardingState,
  loadOnboardingState,
  saveOnboardingState,
} from './shared'
import { getPersona } from './personas'
import { trackEvent } from '@/utils/analytics'
import CreatePlatform from './steps/CreatePlatform'
import SelectClient from './steps/SelectClient'
import CreateAssistant from './steps/CreateAssistant'
import SetupHub from './steps/SetupHub'
import Launch from './steps/Launch'

const DEFAULT_FORM = {
  assistantName: 'Meu primeiro agente',
  personaId: 'sales',
  platformName: '',
}

function toPlatformSummary(platform) {
  if (!platform?.id) return null
  return {
    id: platform.id,
    name: platform.name,
    logo: platform.logo,
    primaryColor: platform.primaryColor,
    isActive: platform.isActive,
  }
}

export default function Onboarding() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [user] = useContext(UserContext)
  const mode = searchParams.get('flow') === 'agent' ? 'agent' : 'client'
  const requestedClientId = searchParams.get('clientId')

  const initial = useState(() => {
    const saved = loadOnboardingState()
    if (saved?.mode !== mode) return null
    if (
      mode === 'agent' &&
      requestedClientId &&
      Number(saved?.createdPlatform?.id) !== Number(requestedClientId)
    ) {
      return null
    }
    return saved
  })[0]

  const [currentStep, setCurrentStep] = useState(initial?.currentStep ?? 0)
  const [createdPlatform, setCreatedPlatform] = useState(initial?.createdPlatform ?? null)
  const [createdAssistant, setCreatedAssistant] = useState(initial?.createdAssistant ?? null)
  const [completedSteps, setCompletedSteps] = useState(initial?.completedSteps ?? new Set())
  const [integrationStatus, setIntegrationStatus] = useState(initial?.integrationStatus ?? { channel: false })
  const [setupProgress, setSetupProgress] = useState(initial?.setupProgress ?? DEFAULT_SETUP_PROGRESS)
  const [platformName, setPlatformName] = useState(initial?.createdPlatform?.name ?? DEFAULT_FORM.platformName)
  const [assistantName, setAssistantName] = useState(initial?.createdAssistant?.name ?? DEFAULT_FORM.assistantName)
  const [personaId, setPersonaId] = useState(initial?.createdAssistant?.personaId ?? DEFAULT_FORM.personaId)
  const [creatingPlatform, setCreatingPlatform] = useState(false)
  const [creatingAssistant, setCreatingAssistant] = useState(false)
  const [clients, setClients] = useState([])
  const [clientsLoading, setClientsLoading] = useState(mode === 'agent')
  const [clientSearch, setClientSearch] = useState('')
  const channelEventTracked = useRef(Boolean(initial?.integrationStatus?.channel))

  useEffect(() => {
    saveOnboardingState({
      currentStep,
      mode,
      createdPlatform,
      createdAssistant,
      completedSteps,
      integrationStatus,
      setupProgress,
    })
  }, [currentStep, mode, createdPlatform, createdAssistant, completedSteps, integrationStatus, setupProgress])

  useEffect(() => {
    if (mode !== 'agent') return
    let active = true
    setClientsLoading(true)

    const request = requestedClientId
      ? getNativeClientPlatform(requestedClientId).then(({ data }) => ({ data: [data] }))
      : getNativeClientPlatforms({ page: 1, perPage: 100 })

    request
      .then(({ data }) => {
        if (!active) return
        const list = data ?? []
        setClients(list)
        if (requestedClientId) {
          const selected = toPlatformSummary(list[0])
          if (selected) setCreatedPlatform(selected)
        }
      })
      .catch(() => {
        if (active) toast.error('Não foi possível carregar os clientes.')
      })
      .finally(() => {
        if (active) setClientsLoading(false)
      })

    return () => {
      active = false
    }
  }, [mode, requestedClientId])

  const filteredClients = useMemo(() => {
    const term = clientSearch.trim().toLowerCase()
    if (!term) return clients
    return clients.filter((client) => client.name?.toLowerCase().includes(term))
  }, [clients, clientSearch])

  const markCompleted = useCallback((id) => {
    setCompletedSteps((previous) => {
      if (previous.has(id)) return previous
      const next = new Set(previous)
      next.add(id)
      return next
    })
  }, [])

  const goNext = useCallback(() => {
    setCurrentStep((step) => Math.min(step + 1, STEPS.length - 1))
  }, [])

  const goBack = useCallback(() => {
    setCurrentStep((step) => Math.max(step - 1, 0))
  }, [])

  const handleExit = useCallback(() => {
    localStorage.setItem(ONBOARDING_SEEN_KEY, '1')
    navigate('/')
  }, [navigate])

  const createPlatformAction = useCallback(async () => {
    const name = platformName.trim()
    if (!name) {
      toast.error('Informe o nome do cliente.')
      return false
    }

    setCreatingPlatform(true)
    try {
      const companyID = user?.companyID || user?.company?.id
      const { data } = await createNativeClientPlatform({
        name,
        companyID,
        isActive: true,
        primaryColor: '#AD34FE',
      })
      const platform = toPlatformSummary(data) ?? { name }
      setCreatedPlatform(platform)
      markCompleted('client')
      trackEvent('onboarding_client_created')
      goNext()
      return true
    } catch (error) {
      console.error(error)
      toast.error('Não foi possível criar o cliente. Tente novamente.')
      return false
    } finally {
      setCreatingPlatform(false)
    }
  }, [platformName, user, markCompleted, goNext])

  const createAssistantAction = useCallback(async () => {
    const name = assistantName.trim()
    if (!name) {
      toast.error('Informe o nome do agente.')
      return false
    }
    if (!createdPlatform?.id) {
      toast.error('Selecione um cliente antes de criar o agente.')
      return false
    }

    setCreatingAssistant(true)
    let assistant = createdAssistant
    try {
      if (!assistant?.id) {
        const persona = getPersona(personaId)
        const { data } = await createAssistant({
          name,
          description: persona?.sample || '',
        })
        if (!data?.id) throw new Error('assistant not created')
        assistant = { ...data, personaId, linked: false }
        setCreatedAssistant(assistant)
      }

      await addAssistantToPlatform(createdPlatform.id, assistant.id)
      setCreatedAssistant({ ...assistant, linked: true })
      markCompleted('agent')
      trackEvent('onboarding_agent_created')
      goNext()
      return true
    } catch (error) {
      console.error(error)
      toast.error(
        assistant?.id
          ? 'O agente foi salvo, mas não pôde ser vinculado. Tente novamente.'
          : 'Não foi possível criar o agente. Tente novamente.'
      )
      return false
    } finally {
      setCreatingAssistant(false)
    }
  }, [assistantName, createdPlatform, createdAssistant, personaId, markCompleted, goNext])

  const handleChannelStatus = useCallback((nextStatus) => {
    if (nextStatus.connected && !channelEventTracked.current) {
      channelEventTracked.current = true
      trackEvent('onboarding_channel_connected')
    }
    setIntegrationStatus((previous) => {
      const next = {
        ...previous,
        channel: nextStatus.connected,
        channelType: nextStatus.type,
        channelLabel: nextStatus.label,
      }
      if (
        previous.channel === next.channel &&
        previous.channelType === next.channelType &&
        previous.channelLabel === next.channelLabel
      ) {
        return previous
      }
      return next
    })
  }, [])

  const updateSetupProgress = useCallback((patch) => {
    setSetupProgress((previous) => {
      const templateIds = patch.templateIds
        ? Array.from(new Set(patch.templateIds))
        : previous.templateIds
      const next = { ...previous, ...patch, templateIds }
      const changed = Object.keys(patch).some((key) => {
        if (key === 'templateIds') {
          return previous.templateIds.length !== templateIds.length || previous.templateIds.some((id, index) => id !== templateIds[index])
        }
        return previous[key] !== next[key]
      })

      return changed ? next : previous
    })
  }, [])

  const completeOnboarding = useCallback(() => {
    clearOnboardingState()
    localStorage.setItem(ONBOARDING_SEEN_KEY, '1')
    trackEvent('onboarding_completed')
  }, [])

  const selectClient = useCallback((client) => {
    setCreatedPlatform(toPlatformSummary(client))
    setCompletedSteps((previous) => {
      const next = new Set(previous)
      next.add('client')
      return next
    })
  }, [])

  const stepConfig = useMemo(() => {
    if (currentStep === 0) {
      if (mode === 'agent') {
        return {
          content: (
            <SelectClient
              clients={filteredClients}
              loading={clientsLoading}
              value={createdPlatform}
              search={clientSearch}
              onSearchChange={setClientSearch}
              onSelect={selectClient}
            />
          ),
          nextLabel: 'Continuar',
          nextDisabled: !createdPlatform?.id,
        }
      }

      const alreadyCreated = Boolean(createdPlatform?.id)
      return {
        content: (
          <CreatePlatform
            user={user}
            platformName={platformName}
            onNameChange={setPlatformName}
          />
        ),
        nextLabel: alreadyCreated ? 'Continuar' : 'Criar cliente',
        nextDisabled: !alreadyCreated && !platformName.trim(),
        nextLoading: creatingPlatform,
        primaryAction: alreadyCreated ? null : createPlatformAction,
      }
    }

    if (currentStep === 1) {
      const alreadyCreated = Boolean(createdAssistant?.id)
      const linked = Boolean(createdAssistant?.linked)
      return {
        content: (
          <CreateAssistant
            user={user}
            name={assistantName}
            onNameChange={setAssistantName}
            personaId={personaId}
            onPersonaChange={setPersonaId}
            platformName={createdPlatform?.name || platformName}
          />
        ),
        nextLabel: alreadyCreated && linked ? 'Continuar' : alreadyCreated ? 'Vincular agente' : 'Criar agente',
        nextDisabled: !alreadyCreated && !assistantName.trim(),
        nextLoading: creatingAssistant,
        primaryAction: alreadyCreated && linked ? null : createAssistantAction,
      }
    }

    if (currentStep === 2) {
      return {
        content: (
          <SetupHub
            assistantID={createdAssistant?.id}
            user={user}
            setupProgress={setupProgress}
            channelStatus={integrationStatus}
            onProgress={updateSetupProgress}
            onChannelStatusChange={handleChannelStatus}
            onTrack={trackEvent}
          />
        ),
        nextLabel: 'Concluir configuração',
        onContinue: () => {
          markCompleted('setup')
          updateSetupProgress({ visited: true, skipped: false })
          trackEvent('onboarding_setup_completed')
        },
      }
    }

    return {
      content: (
        <Launch
          mode={mode}
          user={user}
          createdPlatform={createdPlatform}
          createdAssistant={createdAssistant}
          integrationStatus={integrationStatus}
          setupProgress={setupProgress}
          onFinish={completeOnboarding}
        />
      ),
      hideFooter: true,
    }
  }, [
    currentStep,
    mode,
    filteredClients,
    clientsLoading,
    createdPlatform,
    clientSearch,
    selectClient,
    user,
    platformName,
    creatingPlatform,
    createPlatformAction,
    createdAssistant,
    assistantName,
    personaId,
    creatingAssistant,
    createAssistantAction,
    integrationStatus,
    handleChannelStatus,
    setupProgress,
    updateSetupProgress,
    completeOnboarding,
    markCompleted,
  ])

  const handleNext = useCallback(async () => {
    if (stepConfig.primaryAction) {
      await stepConfig.primaryAction()
      return
    }
    stepConfig.onContinue?.()
    goNext()
  }, [goNext, stepConfig])

  const handleSkip = useCallback(() => {
    if (STEPS[currentStep]?.id === 'setup') {
      updateSetupProgress({ visited: true, skipped: true })
      trackEvent('onboarding_setup_skipped')
    }
    goNext()
  }, [currentStep, goNext, updateSetupProgress])

  return (
    <OnboardingShell
      currentStep={currentStep}
      steps={STEPS}
      onBack={goBack}
      onNext={handleNext}
      onSkip={handleSkip}
      onExit={handleExit}
      nextLabel={stepConfig.nextLabel}
      nextDisabled={stepConfig.nextDisabled}
      nextLoading={stepConfig.nextLoading}
      hideFooter={stepConfig.hideFooter}
    >
      {stepConfig.content}
    </OnboardingShell>
  )
}
